import { Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import type { CatalogMessageId } from '~/i18n';
import { treatmentStartUrgency, type TreatmentStartUrgency } from '../model';

/** How each urgency is drawn: a start far off is muted, a near or past one warns. */
const URGENCY_LOOK = {
    ok: { color: 'text.secondary', labelId: 'patientList.treatmentStart.ok' },
    warn: { color: 'warning.main', labelId: 'patientList.treatmentStart.warn' },
    critical: {
        color: 'error.main',
        labelId: 'patientList.treatmentStart.critical',
    },
    started: {
        color: 'error.main',
        labelId: 'patientList.treatmentStart.started',
    },
} as const satisfies Record<
    Exclude<TreatmentStartUrgency['level'], 'none'>,
    { color: string; labelId: CatalogMessageId }
>;

export type TreatmentStartNoteProps = {
    /** The patient's treatment start ("yyyy-MM-dd") and where its course is. */
    patient: { treatmentStart?: string | null; courseStage?: string | null };
    /** Today, injected so the note is testable. */
    today: Date;
};

/**
 * How far off a treatment start is ("In 12 days", "Critical: starts in 1
 * day", "Started 2 days ago") against the treatment start warning. Renders
 * nothing without a start.
 */
export default function TreatmentStartNote({
    patient,
    today,
}: TreatmentStartNoteProps) {
    const intl = useIntl();
    const urgency = treatmentStartUrgency(patient, today);
    if (urgency.level === 'none') {
        return null;
    }
    const look = URGENCY_LOOK[urgency.level];
    return (
        <Typography
            variant="caption"
            component="div"
            sx={{ mt: 0.25, color: look.color }}
        >
            {intl.formatMessage(
                { id: look.labelId },
                { days: Math.abs(urgency.days) }
            )}
        </Typography>
    );
}
